/**
 * PGSS-5799: what an outgoing shipment needs before it is complete, by its shipment type (the server decides; this
 * only reads what it answered from GET /api/customer-shipments/{id}/steps).
 *
 * - BOL (Customer BOL, LTL): Pallet ready (after the pallets are added on Scan to load) moves it to Ready for BOL; then Mark complete with
 *   the BOL #.
 * - PHOTO (Customer Pickup, Flat bed): a photo, then Mark as shipped.
 * - TRACKING (parcel / courier): a photo and the Tracking #.
 * - NONE: as before.
 */
import { REQUEST_TIMEOUT_MS } from 'app/main/apps/scanToLoad/scanToLoadRules';
import { shipmentLoadApi } from './shipmentLoadRules';

export function shipmentStepsApi(shipmentId) {
  return `/api/customer-shipments/${shipmentId}/steps`;
}

export function palletReadyApi(shipmentId) {
  return `${shipmentLoadApi(shipmentId).replace(/\/load$/, '')}/pallet-ready`;
}

/** The shipment's steps, or null when they can't be read (the page then works as before). */
export async function fetchShipmentSteps(get, shipmentId) {
  try {
    const { data } = await get(shipmentStepsApi(shipmentId), { timeout: REQUEST_TIMEOUT_MS });
    return data && typeof data === 'object' && data.rule ? data : null;
  } catch {
    return null;
  }
}

/** Show Pallet ready: a BOL shipment not yet Ready for BOL and not shipped. */
export function needsPalletReady(steps) {
  return Boolean(steps && steps.rule === 'BOL' && !steps.readyForBol && !steps.closed);
}

/** Why Mark as shipped can't be pressed yet, or ''. */
export function markShippedBlockedReason(steps) {
  if (needsPalletReady(steps)) return 'Press Pallet ready first.';
  return '';
}

/** The number Mark as shipped asks for: BOL # for BOL types, Tracking # for parcel types; null otherwise. */
export function requiredNumber(steps) {
  if (steps?.rule === 'BOL') return { field: 'bol_number', label: 'BOL #', value: steps.bolNumber || '' };
  if (steps?.rule === 'TRACKING') return { field: 'tracking_number', label: 'Tracking #', value: steps.trackingNumber || '' };
  return null;
}

/**
 * PGSS-5803: why Pallet ready can't be pressed yet, or '' (the server's rule). Once picking started on Scan to load it
 * needs a pallet; a shipment that never started picking there (under way at release, or loaded without the pick
 * ticket) needs the pallet photo, as before (review of #4492).
 */
export function palletReadyProblem(steps, uploadedIds) {
  if ((steps?.pallets || 0) > 0) return '';
  if (steps?.pickingStartedAt) return 'Add at least one pallet (with its photo) on Scan to load before Pallet ready.';
  if (!hasPhoto(steps, uploadedIds)) return 'Add the pallet photo (Documents, below) before Pallet ready.';
  return '';
}

const SHIP_TEXT = {
  PHOTO: 'Add a photo, then Mark as Shipped',
  TRACKING: 'Add a photo and the Tracking #, then Mark as Shipped',
};

/**
 * PGSS-5858: what Scan to load tells staff to do next, so finishing the last box never leaves them guessing.
 * `action` is 'palletReady' (the button is here), 'checkList' (the rest is on the Check List), or null (keep going).
 * `left` is how much is still to load, or null until the load has been read: nothing can be said yet, and "All loaded"
 * must not show on a load that was never read (review of #2819).
 */
export function nextStep(steps, left) {
  if (!steps || steps.closed || left === null || left === undefined) return null;
  if (left > 0) {
    // PGSS-5861: shipping without them is a Company Profile switch; when it is off, the rest must be scanned.
    if (steps.allowUnscanned === false) return { text: `${left} still to load. Scan the rest to go on.`, action: null };
    return { text: `${left} still to load. Scan the rest, or finish on the Check List to ship without them.`, action: null };
  }
  if (steps.rule === 'BOL') {
    if (steps.readyForBol) return { text: 'Ready for BOL. Enter the BOL # on the Check List to complete the shipment.', action: 'checkList' };
    if (steps.pickingStartedAt && !(steps.pallets > 0)) return { text: 'All loaded. Add the pallet photo above, then Pallet ready.', action: null };
    if (!steps.pickingStartedAt && !(steps.pallets > 0)) return { text: 'All loaded. Add the pallet photo on the Check List, then Pallet ready.', action: 'checkList' };
    return { text: 'All loaded. Press Pallet ready to move this shipment to Ready for BOL.', action: 'palletReady' };
  }
  return { text: `All loaded. ${SHIP_TEXT[steps.rule] || 'Mark as Shipped'} on the Check List.`, action: 'checkList' };
}

/** True when Mark as shipped has a photo to go on: one uploaded now, or one already on the shipment. */
export function hasPhoto(steps, uploadedIds) {
  return (Array.isArray(uploadedIds) && uploadedIds.length > 0) || (steps?.photos || 0) > 0;
}

/** "Oct 22, 2026" in PSG's time zone; the ISO text if it isn't a date. */
export function stepDate(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso ?? '');
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'America/New_York' });
}

/**
 * PGSS-5802: the shipment's timeline, one entry per step that happened: on the calendar (created), Ready for BOL,
 * shipped, then the days to ship. `format` turns an ISO time into the screen's date. PGSS-5846: and the BOL # once it
 * is entered, which nothing on the Check List showed.
 */
export function stepsTimeline(steps, format = stepDate) {
  if (!steps) return [];
  const entries = [];
  if (steps.onCalendarAt) entries.push({ key: 'calendar', label: 'On calendar', value: format(steps.onCalendarAt) });
  if (steps.pickingStartedAt) entries.push({ key: 'picking', label: 'Picking started', value: format(steps.pickingStartedAt) });
  if (steps.readyForBolAt) entries.push({ key: 'bol', label: 'Ready for BOL', value: format(steps.readyForBolAt) });
  if (steps.shippedAt) entries.push({ key: 'shipped', label: 'Shipped', value: format(steps.shippedAt) });
  if (steps.bolNumber) entries.push({ key: 'bolNumber', label: 'BOL #', value: steps.bolNumber });
  if (steps.daysToShip !== null && steps.daysToShip !== undefined) {
    entries.push({ key: 'days', label: 'Days to ship', value: String(steps.daysToShip) });
  }
  return entries;
}

/** PGSS-5861: each step's name on the Check List stepper. */
const STEP_NAMES = {
  PICK: 'Pick',
  LOAD: 'Load & scan',
  PALLET_READY: 'Pallet ready',
  PHOTO: 'Photo',
};

function completeName(rule) {
  if (rule === 'BOL') return 'BOL #';
  if (rule === 'TRACKING') return 'Tracking #';
  return 'Mark as shipped';
}

/**
 * PGSS-5861: the Check List stepper: the shipment type's steps, then Shipped, each 'done', 'current' or 'todo' (the
 * server's `stepCodes` and `step`, 1-based; step 0 is awaiting approval). Empty on an older server, which sends no
 * steps: the page then works as before.
 */
export function stepperItems(steps) {
  const codes = Array.isArray(steps?.stepCodes) ? steps.stepCodes : [];
  if (!codes.length || !Number.isInteger(steps.step)) return [];
  const items = codes.map((code) => ({ code, label: code === 'COMPLETE' ? completeName(steps.rule) : STEP_NAMES[code] || code }));
  items.push({ code: 'DONE', label: 'Shipped' });
  return items.map((item, i) => {
    const n = i + 1;
    let state = 'todo';
    if (n < steps.step || steps.nextAction === 'DONE') state = 'done';
    else if (n === steps.step) state = 'current';
    return { ...item, state };
  });
}

/**
 * PGSS-5861: the "Next" panel: what to do now, why it can't be done yet (blockers), and which control does it:
 * 'scan' (Scan to load: picking and loading), 'palletReady', 'photo' (Documents, below), 'complete' (Mark complete /
 * Mark as shipped) or 'approve' (on the calendar). `uploadedIds` are photos added on the page and not yet saved. Null
 * when shipped or on an older server.
 */
export function nextPanel(steps, uploadedIds) {
  const action = steps?.nextAction;
  if (!action || action === 'DONE') return null;
  // The photo is added on this page (Documents) before it is saved: once there is one, the type's last step is next.
  if (action === 'PHOTO' && hasPhoto(steps, uploadedIds)) {
    const label = steps.rule === 'TRACKING' ? 'Enter the Tracking # to ship' : 'Mark as shipped';
    return { action: 'COMPLETE', control: 'complete', label, blockers: [] };
  }
  const control = {
    PICK: 'scan', LOAD: 'scan', PALLET_READY: 'palletReady', PHOTO: 'photo', COMPLETE: 'complete', APPROVE: 'approve',
  }[action] || null;
  return {
    action,
    control,
    label: steps.nextLabel || '',
    blockers: Array.isArray(steps.blockers) ? steps.blockers : [],
  };
}

/**
 * PGSS-5861: going on with boxes not scanned is a Company Profile switch ("Allow Pallet Ready Without Scanning").
 * When it is off this returns the message to show instead of offering "anyway"; '' when it is on, or when the
 * steps couldn't be read (as before: the server still refuses if it is off).
 */
export function unscannedRefusal(steps, count) {
  if (!steps || steps.allowUnscanned !== false || !count) return '';
  return `${count} still to load. Scan them first: going on with items not scanned is turned off in Company Profile.`;
}
